import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';

export const DefaultDropdown = ({
  items,
  onItemSelect,
  title,
  className = '',
  maxHeight = '',
  errorClass = '',
  id = 'default-dropdown',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const listboxId = `${id}-listbox`;

  const handleButtonClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
    setActiveIndex(-1);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleButtonClick(e);
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      setIsOpen(false);
      setActiveIndex(-1);
      buttonRef.current?.focus();
    }
  }, [handleButtonClick, isOpen]);

  const handleItemSelect = useCallback((item, index, e) => {
    e.preventDefault();
    e.stopPropagation();

    onItemSelect(item);
    setIsOpen(false);
    setActiveIndex(-1);
    buttonRef.current?.focus();
  }, [onItemSelect]);

  const handleItemKeyDown = useCallback((e, item, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleItemSelect(item, index, e);
    }
  }, [handleItemSelect]);


  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
    setActiveIndex(-1);
  });

  useLayoutEffect(() => {
    const checkDropdownPosition = () => {
      if (!buttonRef.current || !dropdownRef.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const dropdownHeight = dropdownRef.current.offsetHeight;

      setDropdownPosition(spaceBelow < dropdownHeight ? 'top' : 'bottom');
    };

    if (isOpen) {
      checkDropdownPosition();
    }
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
    >
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        type="button"
        tabIndex={0}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        className={`${errorClass} w-full text-left px-3 py-2 bg-[#FDFDFF]
          rounded-md flex justify-between items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
          ${isOpen ? 'border border-[#006EB7]' : 'border border-[#CBCDD6]'
    }`}
      >
        <span className="text-[#0C0D1A]">{title}</span>
        <ChevronDown
          className={`w-5 h-5 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          className={`
            absolute z-10 w-full bg-white 
            border border-[#006EB7] rounded-md 
            shadow-lg 
            ${maxHeight} 
            overflow-y-auto 
            scrollbar-thin 
            scrollbar-track-gray-100 
            scrollbar-thumb-gray-300
            ${dropdownPosition === 'top' ? 'bottom-full mb-1' : 'mt-1'}
          `}
        >
          {items.map((item, index) => (
            <div
              key={index}
              role="option"
              tabIndex={0}
              aria-selected={activeIndex === index}
              onClick={(e) => handleItemSelect(item, index, e)}
              onKeyDown={(e) => handleItemKeyDown(e, item, index)}
              className={`
                px-3 py-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset
                ${activeIndex === index ? 'bg-gray-200' : 'hover:bg-gray-100 focus:bg-gray-100'}
              `}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
